import { useSelector } from "react-redux";
import AsyncSelect from "react-select/async";
import CampaignsMenu from "../../components/campaignsMenu/CampaignsMenu";
import SubmitButton from "../../components/submitButton/SubmitButton";
import Datepicker from "tailwind-datepicker-react";
import React, { useState } from "react";
import { useNotifications } from "../../context/Notifications";
import { useTranslator } from "../../context/translator";
import {
  useGetCompanyQuery,
  useGetTemplatesQuery,
  useGetUserByIdQuery,
} from "store/api/apiSlice";
import { createCampaignService } from "services/campaign/services";
import { getCampaignListService } from "services/campaignList/services";
import FieldLabel from "components/FieldLabel/FieldLabel";
import TypePill from "components/typePill/TypePill";
import SectionLabel from "components/sectionLabel/SectionLabel";
import { useNavigate } from "react-router-dom";

const campaignTypes = [
  { _id: "6a1f7d0673e7c1bdd3ecbc05", type: "EMAIL", name: "Email" },
  { _id: "6a30e6c4b8c96680411c8313", type: "SMS", name: "SMS" },
];

/* ─── Base input classes (light + dark) ─────────────────────────────── */
const inputCls =
  "w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-100 px-3.5 py-2.5 outline-none transition focus:border-blue-600 dark:focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-500/20 placeholder:text-gray-400 dark:placeholder:text-gray-500";

/* ─── Select styles — light & dark variants ──────────────────────────── */
const buildSelectStyles = (isDark) => ({
  control: (base, { isFocused }) => ({
    ...base,
    borderRadius: "12px",
    borderColor: isFocused ? "#1a56db" : isDark ? "#374151" : "#E5E7EB",
    boxShadow: isFocused ? "0 0 0 2px rgba(26,86,219,0.12)" : "none",
    backgroundColor: isDark ? "#1f2937" : "#F9FAFB",
    padding: "2px 4px",
    fontSize: "14px",
    "&:hover": { borderColor: "#1a56db" },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "14px",
    boxShadow: isDark
      ? "0 10px 30px rgba(0,0,0,0.4)"
      : "0 10px 30px rgba(15,23,42,0.12)",
    border: `1px solid ${isDark ? "#374151" : "#E5E7EB"}`,
    backgroundColor: isDark ? "#1f2937" : "#fff",
    overflow: "hidden",
    zIndex: 60,
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    fontSize: "14px",
    backgroundColor: isSelected
      ? "#1a56db"
      : isFocused
        ? isDark
          ? "#1e3a5f"
          : "#EFF6FF"
        : "transparent",
    color: isSelected ? "#fff" : isDark ? "#e5e7eb" : "#1E293B",
    cursor: "pointer",
  }),
  placeholder: (base) => ({
    ...base,
    color: isDark ? "#6b7280" : "#9CA3AF",
    fontSize: "14px",
  }),
  singleValue: (base) => ({
    ...base,
    color: isDark ? "#e5e7eb" : "#1E293B",
    fontSize: "14px",
  }),
  input: (base) => ({ ...base, color: isDark ? "#e5e7eb" : "#1E293B" }),
});

/* ─── Icons ─────────────────────────────────────────────────────────── */
const EmailIcon = () => (
  <svg
    width="15"
    height="15"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);
const SmsIcon = () => (
  <svg
    width="15"
    height="15"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 10h.01M12 10h.01M16 10h.01M21 16c0 1.1-.9 2-2 2H7l-4 4V6c0-1.1.9-2 2-2h14c1.1 0 2 .9 2 2v10z"
    />
  </svg>
);

/* ─── Main component ─────────────────────────────────────────────────── */
const NewCampaign = () => {
  const { user } = useSelector((state) => state.user);
  const { addNotification } = useNotifications();
  const { translate } = useTranslator();

  const [isLoading, setIsLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState("14:00");
  const navigate = useNavigate();

  // Detect dark mode from the DOM so react-select can use the right palette
  const isDark =
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");
  const selectStyles = buildSelectStyles(isDark);

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    { skip: !userProfile?.user?.companyId },
  );

  const { data: templates, isLoading: templatesLoading } = useGetTemplatesQuery(
    userProfile?.user?.companyId,
    { skip: !userProfile?.user?.companyId },
  );

  const [campaign, setCampaign] = useState({
    name: "",
    description: "",
    status: "ACTIVE",
    date: new Date().toISOString(),
    userId: user.id,
    companyId: "",
    listCustomerId: "SE",
    campaignTypeId: campaignTypes[0]._id,
    templateId: "",
  });

  const selectedCampaignType = campaignTypes.find(
    (t) => t._id === campaign.campaignTypeId,
  );

  const filteredTemplates =
    templates?.templates?.filter((template) => {
      const type = selectedCampaignType?.type?.toLowerCase();
      return (
        template.type?.toLowerCase() === type &&
        !template.resetPassword &&
        !template.newUser
      );
    }) ?? [];

  const [dateOptions] = useState({
    title: translate("date"),
    autoHide: true,
    todayBtn: true,
    clearBtn: false,
    clearBtnText: translate("clear"),
    maxDate: new Date("2040-01-01"),
    minDate: new Date(),
    theme: {
      background: "bg-gray-50 dark:bg-gray-700",
      text: "text-gray-900 dark:text-white",
      disabledText: "text-slate-300",
      input: "h-11",
    },
    icons: {
      prev: () => <span>{translate("previous")}</span>,
      next: () => <span>{translate("next")}</span>,
    },
    datepickerClassNames: "absolute z-50",
    defaultDate: new Date(),
    language: "en",
    disabledDates: [],
    weekDays: [
      translate("mo"),
      translate("tu"),
      translate("we"),
      translate("th"),
      translate("fr"),
      translate("sa"),
      translate("su"),
    ],
    inputNameProp: "date",
    inputIdProp: "date",
    inputPlaceholderProp: translate("select-date"),
  });

  const updateCampaignDate = (date, time) => {
    const [hours, minutes] = time.split(":");
    const finalDate = new Date(date);
    finalDate.setHours(Number(hours));
    finalDate.setMinutes(Number(minutes));
    finalDate.setSeconds(0);
    finalDate.setMilliseconds(0);
    setCampaign((prev) => ({ ...prev, date: finalDate.toISOString() }));
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    updateCampaignDate(date, selectedTime);
  };

  const handleTimeChange = (e) => {
    const time = e.target.value;
    setSelectedTime(time);
    updateCampaignDate(selectedDate, time);
  };

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setCampaign((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "campaignTypeId") updated.templateId = "";
      return updated;
    });
  };

  const handleSelectListChange = (selectedOption) => {
    setCampaign((prev) => ({ ...prev, listCustomerId: selectedOption.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoading(true);
    try {
      const response = await createCampaignService({
        data: { ...campaign, userId: user.id, companyId: company?._id },
      });
      if (response?.campaign?.success) {
        addNotification({
          id: Date.now(),
          message: translate("campaign-created"),
          kind: "success",
        });
        navigate("/campaigns");
      } else {
        addNotification({
          id: Date.now(),
          message: translate("error-creating-campaign"),
          kind: "danger",
        });
      }
    } catch {
      addNotification({
        id: Date.now(),
        message: translate("error-creating-campaign"),
        kind: "danger",
      });
    }
    setIsLoading(false);
  };

  const loadOptions = async (inputValue) => {
    const data = await getCampaignListService({
      companyId: company?._id,
      page: 1,
      search: inputValue,
      category: "all",
      communications: "all",
      pageSize: 3,
      status: "ACTIVE",
    });
    const list = data?.campaignLists ?? data?.data ?? data ?? [];
    return list.map((c) => ({
      value: c._id ?? c.id,
      label: c.name ?? `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim(),
    }));
  };

  if (userLoading || companyLoading || templatesLoading) return null;

  return (
    <CampaignsMenu>
      <div className="max-w-3xl mx-auto">
        {/* ── Page header ── */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-1 h-10 rounded-full bg-blue-700 dark:bg-blue-500" />
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-blue-700 dark:text-blue-400 mb-0.5">
              {translate("campaigns")}
            </p>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
              {translate("new-campaign")}
            </h2>
          </div>
        </div>

        {/* ── Card ── */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm ring-1 ring-gray-100 dark:ring-gray-700/60 overflow-hidden">
          {/* Top accent bar */}
          <div
            className="h-1.5 w-full"
            style={{
              background:
                "linear-gradient(90deg, rgb(26 86 219) 0%, #60a5fa 100%)",
            }}
          />

          <form
            onSubmit={handleSubmit}
            className="p-6 md:p-8 flex flex-col gap-8"
          >
            {/* ── Section 1 · Identity ── */}
            <div>
              <SectionLabel
                icon={
                  <svg
                    width="14"
                    height="14"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                }
              >
                {translate("identity")}
              </SectionLabel>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <FieldLabel htmlFor="name">{translate("name")}</FieldLabel>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={campaign.name}
                    onChange={handleOnChange}
                    required
                    placeholder="e.g. Summer Promo 2026"
                    className={inputCls}
                  />
                </div>

                <div className="md:col-span-2">
                  <FieldLabel htmlFor="description">
                    {translate("description")}
                  </FieldLabel>
                  <textarea
                    id="description"
                    name="description"
                    rows={3}
                    value={campaign.description}
                    onChange={handleOnChange}
                    required
                    placeholder="Briefly describe the goal of this campaign…"
                    className={`${inputCls} resize-none`}
                  />
                </div>
              </div>
            </div>

            <div className="h-px bg-gray-100 dark:bg-gray-700/60" />

            {/* ── Section 2 · Channel & Audience ── */}
            <div>
              <SectionLabel
                icon={
                  <svg
                    width="14"
                    height="14"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H4v-2a4 4 0 014-4h1m4-4a4 4 0 110-8 4 4 0 010 8z"
                    />
                  </svg>
                }
              >
                {translate("channel-audience")}
              </SectionLabel>

              <div className="grid gap-4 md:grid-cols-2">
                {/* Campaign type pill toggle */}
                <div className="md:col-span-2">
                  <FieldLabel>{translate("campaign-type")}</FieldLabel>
                  <div className="flex gap-3">
                    {campaignTypes.map((type) => (
                      <TypePill
                        key={type._id}
                        label={type.name}
                        icon={
                          type.type === "EMAIL" ? <EmailIcon /> : <SmsIcon />
                        }
                        active={campaign.campaignTypeId === type._id}
                        onClick={() =>
                          setCampaign((prev) => ({
                            ...prev,
                            campaignTypeId: type._id,
                            templateId: "",
                          }))
                        }
                      />
                    ))}
                  </div>
                </div>

                {/* Customer list */}
                <div>
                  <FieldLabel htmlFor="listCustomerId">
                    {translate("customer-list")}
                  </FieldLabel>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={loadOptions}
                    onChange={handleSelectListChange}
                    placeholder="Search a list…"
                    noOptionsMessage={({ inputValue }) =>
                      inputValue ? "No list found" : "Start typing to search"
                    }
                    loadingMessage={() => "Searching…"}
                    styles={selectStyles}
                    formatOptionLabel={(option) => (
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        {option.label}
                      </p>
                    )}
                  />
                </div>

                {/* Template */}
                <div>
                  <FieldLabel htmlFor="templateId">
                    {translate("template")}
                  </FieldLabel>
                  <select
                    id="templateId"
                    name="templateId"
                    value={campaign.templateId}
                    onChange={handleOnChange}
                    required
                    className={inputCls}
                  >
                    <option value="">{translate("select-template")}</option>
                    {filteredTemplates.map((template) => (
                      <option key={template._id} value={template._id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                  {filteredTemplates.length === 0 && (
                    <p className="mt-1.5 text-xs text-amber-500 dark:text-amber-400 flex items-center gap-1">
                      <svg
                        width="12"
                        height="12"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                        />
                      </svg>
                      No {selectedCampaignType?.name} templates found
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="h-px bg-gray-100 dark:bg-gray-700/60" />

            {/* ── Section 3 · Schedule ── */}
            <div>
              <SectionLabel
                icon={
                  <svg
                    width="14"
                    height="14"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                }
              >
                {translate("schedule")}
              </SectionLabel>

              <div className="grid gap-4 md:grid-cols-2">
                {/* Date */}
                <div className="relative">
                  <FieldLabel>{translate("date")}</FieldLabel>
                  <Datepicker
                    options={dateOptions}
                    onChange={handleDateChange}
                    show={show}
                    setShow={setShow}
                  />
                </div>

                {/* Time */}
                <div>
                  <FieldLabel htmlFor="time">{translate("time")}</FieldLabel>
                  <input
                    type="time"
                    id="time"
                    value={selectedTime}
                    onChange={handleTimeChange}
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Schedule summary chip */}
              {selectedDate && selectedTime && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/30 ring-1 ring-blue-100 dark:ring-blue-700/40">
                  <svg
                    width="13"
                    height="13"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="#1a56db"
                    strokeWidth={2.2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">
                    Sending on{" "}
                    {new Date(
                      (() => {
                        const [h, m] = selectedTime.split(":");
                        const d = new Date(selectedDate);
                        d.setHours(+h, +m, 0, 0);
                        return d;
                      })(),
                    ).toLocaleString(undefined, {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              )}
            </div>

            {/* ── Footer ── */}
            <div className="flex justify-end pt-2 border-t border-gray-100 dark:border-gray-700/60">
              <SubmitButton
                isLoading={isLoading}
                text={translate("save")}
                textLoading={translate("saving") + "…"}
                key="saveCampaign"
              />
            </div>
          </form>
        </div>
      </div>
    </CampaignsMenu>
  );
};

export default NewCampaign;
